import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import axios from "axios";
import {
  FaPlus,
  FaFolderOpen,
  FaTicketAlt,
  FaCheck,
  FaTimes,
  FaClock,
  FaBan,
  FaSpinner,
  FaChartLine,
  FaCoins,
  FaBullseye,
  FaArrowRight,
  FaEdit,
  FaTrash,
  FaChevronDown,
  FaChevronUp,
  FaLock,
  FaPlay,
} from "react-icons/fa";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
};

const Projects = () => {
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);

  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [projectToRename, setProjectToRename] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const [expandedProject, setExpandedProject] = useState(null);

  const [isAllowed, setIsAllowed] = useState(false);

  const [form, setForm] = useState({
    name: "",
    type: "simple",
    target_odd: "",
    initial_stake: "",
    total_steps: "",
  });

  const fetchProjects = async () => {

    setLoading(true);

    try {
      const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/projects`;

      const response = await axios.get(baseUrl, {
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
      });

      setProjects(response.data.projects || []);
    } catch (error) {
      console.error("Fetch projects error:", error);

      if (error.response?.status === 401) {
        navigate("/login");
      }

      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const resetForm = () => {
    setForm({
      name: "",
      type: "simple",
      target_odd: "",
      initial_stake: "",
      total_steps: "",
    });
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) return;

    setCreating(true);

    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        target_odd:
          form.target_odd !== "" ? Number(form.target_odd) : 1,
        initial_stake:
          form.initial_stake !== "" ? Number(form.initial_stake) : 1,
        total_steps:
          form.total_steps !== "" ? Number(form.total_steps) : null,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/projects`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        },
      );

      if (response.data.project) {
        setProjects((prev) => [response.data.project, ...prev]);
      } else {
        await fetchProjects();
      }
      // just? await fetchProjects();

      resetForm();
      setShowCreateModal(false);
    } catch (error) {
      console.error("Create project error:", error);

      alert(
        error.response?.data?.error ||
          "Failed to create project.",
      );
    } finally {
      setCreating(false);
    }
  };

  const deleteProject = async (projectId) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this project? This will also delete its tickets.",
    );

    if (!confirmed) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/projects/${projectId}`,
        {
          headers: {
            ...getAuthHeaders(),
          },
        },
      );

      setProjects((prev) =>
        prev.filter(
          (project) => String(project.id) !== String(projectId)
        )
      );

      setExpandedProject((prev) =>
        String(prev) === String(projectId) ? null : prev
      );
    } catch (error) {
      console.error("Delete project error:", error);

      alert(
        error.response?.data?.error ||
          "Failed to delete project.",
      );
    }
  };

  const handleNewTicket = (project) => {
    if (!project) {
        alert("Please select a project first.");
        return;
    }

    navigate("/tickets/new", {
        state: {
        project: project,
        },
    });
  };

  const openRenameModal = (project) => {
    setProjectToRename(project);
    setRenameValue(project.name || "");
    setShowRenameModal(true);
  };

  const handleRenameProject = async (e) => {
    e.preventDefault();

    const name = renameValue.trim();

    if (!name || !projectToRename) {
      return;
    }

    setRenaming(true);

    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/projects/${projectToRename.id}`,
        { name },
        {
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
        }
      );

      setProjects((prev) =>
        prev.map((project) =>
          String(project.id) === String(projectToRename.id)
            ? { ...project, name }
            : project
        )
      );

      setShowRenameModal(false);
      setProjectToRename(null);
      setRenameValue("");
    } catch (error) {
      console.error("Rename project error:", error);

      alert(
        error.response?.data?.error ||
          "Failed to rename project."
      );
    } finally {
      setRenaming(false);
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case "won":
        return {
          label: "COMPLETED",
          className:
            "bg-green-500/10 text-green-400 border-green-500/30",
          dotClass: "bg-green-400",
        };

      case "lost":
        return {
          label: "LOST",
          className:
            "bg-red-500/10 text-red-400 border-red-500/30",
          dotClass: "bg-red-400",
        };

      case "cancelled":
        return {
          label: "CANCELLED",
          className:
            "bg-gray-500/10 text-gray-400 border-gray-500/30",
          dotClass: "bg-gray-400",
        };

      default:
        return {
          label: "IN PROGRESS",
          className:
            "bg-green-500/10 text-green-400 border-green-500/30",
          dotClass: "bg-green-400",
        };
    }
  };

  const projectStats = useMemo(() => {
    return {
      total: projects.length,
      active: projects.filter((p) => p.status === "active").length,
      won: projects.filter((p) => p.status === "won").length,
      lost: projects.filter((p) => p.status === "lost").length,
    };
  }, [projects]);

  const formatMoney = (value, currency = "EUR") => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    return new Intl.NumberFormat("de-AT", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value));
  };

  const formatPercent = (value) => {
    return `${Number(value ?? 0).toFixed(1)}%`;
  };

  const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleDateString("en-CA", {
      timeZone: "Europe/Vienna",
    });
  };

  const toggleProject = (id) => {
    setExpandedProject((prev) => (prev === id ? null : id));
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
          <div className="text-center">
            <FaSpinner className="inline-block animate-spin text-4xl text-green-400 mb-4" />
            <p className="text-gray-300">Loading projects...</p>
            <p className="text-gray-500 text-sm mt-2">
              Please wait
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto p-4 md:p-6">

          {/* HEADER */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                <FaChartLine className="text-green-400" />

                <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                  My Projects
                </span>
              </h1>

              <p className="text-gray-400 text-sm mt-1">
                Track your betting strategies, tickets and results.
              </p>
            </div>

            <div className="flex items-right gap-3">
              <button
                onClick={() => navigate("/tickets")}
                className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500 transition-all shadow-lg shadow-green-600/20 flex items-center justify-center gap-2"
              >
                <FaTicketAlt />
                View Tickets
              </button>

              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500 transition-all shadow-lg shadow-green-600/20 flex items-center justify-center gap-2"
              >
                <FaPlus />
                New Project
              </button>
          </div>
            
          </div>

          {/* EMPTY STATE */}
          {projects.length === 0 ? (
            <div className="bg-gray-800/60 backdrop-blur-sm border border-green-500/20 rounded-xl p-10 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-green-900/30 flex items-center justify-center">
                <FaFolderOpen className="text-3xl text-green-400" />
              </div>

              <h2 className="text-xl font-semibold text-white">
                 No projects yet
              </h2>

              <p className="text-gray-400 text-sm mt-2 max-w-md mx-auto">
                Create your first project to start tracking your
                tickets and strategy performance.
              </p>

              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-5 px-5 py-2.5 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500 transition-all"
              >
                <FaPlus className="inline mr-2" />
                Create Project
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {projects.map((project) => {
              const status = getStatusConfig(project.status);
              const expanded = expandedProject === project.id;

              const currency = project.currency || "EUR";
              const isAdvanced = project.type === "advanced";

              const totalSteps = Number(project.total_steps || 0);
              const activeStep = Number(
                project.active_step_number || project.current_step || 0
              );

              const currentStep = Math.min(
                totalSteps,
                Math.max(0, activeStep)
              );

              const progress =
                totalSteps > 0
                  ? Math.min(100, (currentStep / totalSteps) * 100)
                  : 0;

              const ticketCount = Number(project.ticket_count || 0);
              const won = Number(project.won_count || 0);
              const lost = Number(project.lost_count || 0);
              const pending = Number(project.pending_count || 0);
              const invested = Number(project.total_invested || 0);
              const profit = Number(project.total_profit || 0);
              const roi = Number(project.roi || 0);

              const StatRow = ({ label, value, valueClass = "text-gray-100" }) => (
                <div className="flex items-center justify-between gap-4 py-1.5">
                  <span className="text-sm text-gray-400">{label}</span>
                  <span
                    className={`text-sm md:text-base font-semibold text-right ${valueClass}`}
                  >
                    {value}
                  </span>
                </div>
              );

              return (
                <div
                  key={project.id}
                  className="overflow-hidden rounded-3xl border border-gray-700/80 bg-[#1b1e25] shadow-xl shadow-black/10"
                >
                  {/* HEADER */}
                  <div className="p-5 md:p-8">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <h2 className="break-words text-xl md:text-2xl font-bold text-gray-100">
                          {project.name}
                        </h2>

                        <div
                          className={`mt-4 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold ${status.className}`}
                        >
                          <span
                            className={`h-2.5 w-2.5 rounded-full ${status.dotClass}`}
                          />
                          {status.label}
                        </div>
                      </div>

                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openRenameModal(project)}
                          title="Rename project"
                          aria-label={`Rename ${project.name}`}
                          className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-800 text-gray-200 transition hover:bg-gray-700 hover:text-green-400"
                        >
                          <FaEdit />
                        </button>

                        <button
                          type="button"
                          onClick={() => deleteProject(project.id)}
                          title="Delete project"
                          className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-800 text-gray-400 transition hover:bg-red-950/50 hover:text-red-400"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>

                    {/* ADVANCED PROJECT PROGRESS */}
                    {isAdvanced && totalSteps > 0 && (
                      <div className="mt-7">
                        <div className="mb-5 flex items-center justify-between gap-3">
                          <span className="text-sm text-gray-400">
                            Strategy progress
                          </span>

                          <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm font-bold text-amber-400">
                            STEP {currentStep} / {totalSteps}
                          </span>
                        </div>

                        <div className="relative px-1">
                          {/* Connecting line */}
                          <div className="absolute left-5 right-5 top-5 h-1 bg-gray-700 md:left-8 md:right-8">
                            <div
                              className="h-full bg-green-500 transition-all duration-300"
                              style={{ width: `${progress}%` }}
                            />
                          </div>

                          <div
                            className="relative grid"
                            style={{
                              gridTemplateColumns: `repeat(${totalSteps}, minmax(0, 1fr))`,
                            }}
                          >
                            {Array.from(
                              { length: totalSteps },
                              (_, index) => {
                                const step = index + 1;
                                const completed = step < currentStep;
                                const active = step === currentStep;
                                const upcoming = step > currentStep;

                                return (
                                  <div
                                    key={step}
                                    className="flex min-w-0 flex-col items-center"
                                  >
                                    <div
                                      className={`z-10 flex h-10 w-10 items-center justify-center rounded-full border-4 transition-all md:h-12 md:w-12 ${
                                        completed
                                          ? "border-green-500 bg-green-500 text-gray-950"
                                          : active
                                            ? "border-amber-500/30 bg-amber-500 text-gray-950 shadow-lg shadow-amber-500/20"
                                            : "border-gray-700 bg-[#1b1e25] text-gray-500"
                                      }`}
                                      title={`Step ${step}`}
                                    >
                                      {completed ? (
                                        <FaCheck />
                                      ) : active ? (
                                        <FaPlay className="text-sm" />
                                      ) : (
                                        <FaLock className="text-xs" />
                                      )}
                                    </div>

                                    <span
                                      className={`mt-3 text-[10px] font-bold sm:text-xs ${
                                        completed
                                          ? "text-green-400"
                                          : active
                                            ? "text-amber-400"
                                            : "text-gray-500"
                                      }`}
                                    >
                                      STEP {step}
                                    </span>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* DIVIDER */}
                    <div className="my-6 border-t border-gray-700/60" />

                    {/* MAIN STATISTICS */}
                    <div className="grid grid-cols-1 gap-x-10 gap-y-2 sm:grid-cols-2 md:gap-x-12">
                      <div>
                        <StatRow
                          label="Executions"
                          value={ticketCount}
                        />

                        <StatRow
                          label="Steps Failed"
                          value={lost}
                          valueClass="text-red-400"
                        />

                        <StatRow
                          label="Total Invested"
                          value={formatMoney(invested, currency)}
                        />

                        <StatRow
                          label="ROI"
                          value={formatPercent(roi)}
                          valueClass={roi >= 0 ? "text-green-400" : "text-red-400"}
                        />
                      </div>

                      <div>
                        <StatRow
                          label="Steps Completed"
                          value={won}
                          valueClass="text-green-400"
                        />

                        <StatRow
                          label="Pending"
                          value={pending}
                          valueClass="text-amber-400"
                        />

                        <StatRow
                          label="Total Profit"
                          value={formatMoney(profit, currency)}
                          valueClass={profit >= 0 ? "text-green-400" : "text-red-400"}
                        />

                        <StatRow
                          label="Last Used"
                          value={
                            project.updated_at
                              ? formatDate(project.updated_at)
                              : "-"
                          }
                        />
                      </div>
                    </div>

                    {/* ADVANCED PROJECT FINANCIAL DETAILS */}
                    {isAdvanced && (
                      <>
                        <div className="my-6 border-t border-gray-700/60" />

                        <div className="grid grid-cols-1 gap-x-10 sm:grid-cols-2 md:gap-x-12">
                          <StatRow
                            label="Current Stake"
                            value={formatMoney(project.current_stake, currency)}
                            valueClass="text-amber-400"
                          />

                          <StatRow
                            label="Target Odds"
                            value={Number(project.target_odd || 0).toFixed(2)}
                            valueClass="text-blue-400"
                          />

                          {totalSteps > 0 && (
                            <StatRow
                              label="Steps Remaining"
                              value={Math.max(0, totalSteps - currentStep + 1)}
                            />
                          )}

                          <StatRow
                            label="Initial Stake"
                            value={formatMoney(project.initial_stake, currency)}
                          />
                        </div>
                      </>
                    )}

                    {/* FOOTER ACTIONS */}
                    <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-700/60 pt-5">
                      <span className="text-xs text-gray-500">
                        Created {formatDate(project.created_at)}
                      </span>

                      <div className="flex items-right gap-3">
                        {project.type === 'simple' ? (
                          <button
                            onClick={() => handleNewTicket(project)}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                                project
                                ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500"
                                : "bg-gray-700 text-gray-500 cursor-not-allowed"
                            }`}
                            >
                            <FaPlus className="inline mr-2" />
                            New Ticket
                          </button>
                        ) : (
                          !pending && (
                            <button
                              onClick={() => handleNewTicket(project)}
                              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500`}
                              >
                              <FaPlus className="inline mr-2" />
                              New Ticket
                            </button>
                          )
                        )}

                        <button
                          type="button"
                          onClick={() => navigate(`/projects/${project.id}`)}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500`}
                        >
                          More Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          )}
        </div>
      </div>

      {/* CREATE PROJECT MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">

          <div className="w-full max-w-lg bg-gray-800 border border-green-500/20 rounded-2xl shadow-2xl">

            <div className="flex items-center justify-between px-5 py-4 border-b border-green-500/10">
              <div>
                <h2 className="text-lg font-semibold text-white">
                  Create Project
                </h2>

                <p className="text-xs text-gray-400 mt-1">
                  Set up your tracking strategy.
                </p>
              </div>

              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-white text-xl"
              >
                ×
              </button>
            </div>

            <form
              onSubmit={handleCreateProject}
              className="p-5 space-y-4"
            >

              {/* NAME */}
              <div>
                <label className="block text-sm text-gray-300 mb-1.5">
                  Project name
                </label>

                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      name: e.target.value,
                    })
                  }
                  placeholder="e.g. Weekend Strategy"
                  required
                  className="w-full px-4 py-2.5 bg-gray-900/70 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* TYPE */}
              <div>
                <label className="block text-sm text-gray-300 mb-1.5">
                  Project type
                </label>

                <div className="grid grid-cols-2 gap-3">

                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        type: "simple",
                      })
                    }
                    className={`p-3 rounded-xl border text-left transition-all ${
                      form.type === "simple"
                        ? "border-green-500 bg-green-900/20"
                        : "border-gray-700 bg-gray-900/40"
                    }`}
                  >
                    <p className="text-white font-medium">
                      Simple
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Basic ticket tracking
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        type: "advanced",
                      })
                    }
                    className={`p-3 rounded-xl border text-left transition-all ${
                      form.type === "advanced"
                        ? "border-green-500 bg-green-900/20"
                        : "border-gray-700 bg-gray-900/40"
                    }`}
                  >
                    <p className="text-white font-medium">
                      Advanced
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Step-based strategy
                    </p>
                  </button>

                </div>
              </div>

              {/* TOTAL STEPS */}
              {form.type === "advanced" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-gray-300 mb-1.5">
                      Total steps
                    </label>

                    <input
                      type="number"
                      min="1"
                      value={form.total_steps}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          total_steps: e.target.value,
                        })
                      }
                      placeholder="10"
                      className="w-full px-4 py-2.5 bg-gray-900/70 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-300 mb-1.5">
                      Target odd
                    </label>

                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.target_odd}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          target_odd: e.target.value,
                        })
                      }
                      placeholder="2.00"
                      className="w-full px-4 py-2.5 bg-gray-900/70 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-300 mb-1.5">
                      Initial stake
                    </label>

                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.initial_stake}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          initial_stake: e.target.value,
                        })
                      }
                      placeholder="10.00"
                      className="w-full px-4 py-2.5 bg-gray-900/70 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
              )}

              {/* ACTIONS */}
              <div className="flex gap-3 pt-3">

                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white transition-colors"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500 disabled:opacity-50 transition-all"
                >
                  {creating ? (
                    <>
                      <FaSpinner className="inline animate-spin mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FaPlus className="inline mr-2" />
                      Create Project
                    </>
                  )}
                </button>

              </div>
            </form>
          </div>
        </div>
      )}

      {/* RENAME PROJECT MODAL */}
      {showRenameModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl border border-green-500/20 bg-gray-800 shadow-2xl">

            <div className="flex items-center justify-between border-b border-green-500/10 px-5 py-4">
              <div>
                <h2 className="text-lg font-semibold text-white">
                  Rename Project
                </h2>
                <p className="mt-1 text-xs text-gray-400">
                  Choose a new name for your project.
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowRenameModal(false);
                  setProjectToRename(null);
                  setRenameValue("");
                }}
                className="text-xl text-gray-400 hover:text-white"
                aria-label="Close rename modal"
              >
                ×
              </button>
            </div>

            <form
              onSubmit={handleRenameProject}
              className="space-y-4 p-5"
            >
              <div>
                <label
                  htmlFor="rename-project-name"
                  className="mb-1.5 block text-sm text-gray-300"
                >
                  Project name
                </label>

                <input
                  id="rename-project-name"
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  placeholder="Enter project name"
                  maxLength={255}
                  required
                  autoFocus
                  className="w-full rounded-xl border border-gray-700 bg-gray-900/70 px-4 py-2.5 text-white placeholder-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="flex gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowRenameModal(false);
                    setProjectToRename(null);
                    setRenameValue("");
                  }}
                  disabled={renaming}
                  className="flex-1 rounded-xl bg-gray-700 px-4 py-2.5 text-gray-300 transition-colors hover:bg-gray-600 hover:text-white disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={renaming || !renameValue.trim()}
                  className="flex-1 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 px-4 py-2.5 text-white transition-all hover:from-green-500 hover:to-emerald-500 disabled:opacity-50"
                >
                  {renaming ? (
                    <>
                      <FaSpinner className="mr-2 inline animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaEdit className="mr-2 inline" />
                      Save Name
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Projects;